import { Clock, AlertTriangle, CheckCircle, XCircle, Crown, Zap, Star, Pause } from 'lucide-react'
import { useAssinatura } from '../hooks/useAssinatura'
import { Link } from 'react-router-dom'

interface StatusAssinaturaProps {
    /** No home: esconde o card quando a assinatura já está ativa */
    ocultarQuandoAtiva?: boolean
}

export function StatusAssinatura({ ocultarQuandoAtiva = false }: StatusAssinaturaProps) {
    const { assinatura, loading, getAssinaturaStatus } = useAssinatura()

    if (loading) {
        if (ocultarQuandoAtiva) return null
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6 lg:mb-8">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
            </div>
        )
    }

    const status = getAssinaturaStatus()

    // No home: card só aparece quando a assinatura NÃO está ativa
    if (ocultarQuandoAtiva && status.status === 'ativa') {
        return null
    }

    const getPlanoIcon = (nome: string) => {
        switch (nome) {
            case 'start':
                return <Star className="w-5 h-5 text-blue-600" />
            case 'medium':
                return <Zap className="w-5 h-5 text-purple-600" />
            case 'pro':
                return <Crown className="w-5 h-5 text-yellow-600" />
            default:
                return <Star className="w-5 h-5 text-gray-600" />
        }
    }

    const getStatusColor = () => {
        switch (status.status) {
            case 'trial':
                return status.trial_expirado ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
            case 'ativa':
                return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            case 'cancelada':
                return 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            case 'expirada':
                return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            case 'suspensa':
                return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
            default:
                return 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
        }
    }

    const getStatusIcon = () => {
        switch (status.status) {
            case 'trial':
                return status.trial_expirado ? <XCircle className="w-5 h-5 text-red-600" /> : <Clock className="w-5 h-5 text-blue-600" />
            case 'ativa':
                return <CheckCircle className="w-5 h-5 text-green-600" />
            case 'cancelada':
            case 'expirada':
                return <AlertTriangle className="w-5 h-5 text-red-600" />
            case 'suspensa':
                return <Pause className="w-5 h-5 text-yellow-600" />
            default:
                return <AlertTriangle className="w-5 h-5 text-gray-600" />
        }
    }

    const getStatusMessage = () => {
        switch (status.status) {
            case 'sem_assinatura':
                return (
                    <div className="text-center">
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            Você ainda não possui uma assinatura ativa.
                        </p>
                        <Link
                            to="/planos"
                            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Escolher Plano
                        </Link>
                    </div>
                )

            case 'trial':
                if (status.trial_expirado) {
                    return (
                        <div>
                            <p className="text-red-600 dark:text-red-400 mb-2">
                                ⚠️ Seu período de teste expirou
                            </p>
                            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                                Para continuar usando a plataforma, você precisa ativar sua assinatura.
                            </p>
                            <div className="flex gap-2">
                                <Link
                                    to="/assinar"
                                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Ativar Assinatura
                                </Link>
                            </div>
                        </div>
                    )
                }
                return (
                    <div>
                        <p className="text-blue-600 dark:text-blue-400 mb-2">
                            🎉 Você está no período de teste gratuito
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                            Restam <strong>{status.dias_restantes_trial} dias</strong> para ativar sua assinatura.
                        </p>
                        <Link
                            to="/assinar"
                            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Assinar agora
                        </Link>
                    </div>
                )

            case 'ativa':
                return (
                    <div>
                        <p className="text-green-600 dark:text-green-400 mb-2">
                            ✅ Assinatura ativa
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {status.proximo_pagamento && (
                                <>Próximo pagamento: <strong>{new Date(status.proximo_pagamento).toLocaleDateString('pt-BR')}</strong></>
                            )}
                        </p>
                    </div>
                )

            case 'cancelada':
                return (
                    <div>
                        <p className="text-gray-600 dark:text-gray-400 mb-2">
                            Assinatura cancelada
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                            Você pode reativar sua assinatura a qualquer momento.
                        </p>
                        <Link
                            to="/assinar"
                            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Reativar Assinatura
                        </Link>
                    </div>
                )

            case 'expirada':
                return (
                    <div>
                        <p className="text-red-600 dark:text-red-400 mb-2">
                            Assinatura expirada
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                            Renove seu plano para continuar usando a plataforma.
                        </p>
                        <Link
                            to="/assinar"
                            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Renovar Plano
                        </Link>
                    </div>
                )

            case 'suspensa':
                return (
                    <div>
                        <p className="text-yellow-600 dark:text-yellow-400 mb-2">
                            ⚠️ Assinatura suspensa
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                            Sua assinatura foi temporariamente suspensa. Entre em contato com o suporte ou renove o plano.
                        </p>
                        <Link
                            to="/assinar"
                            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Renovar Plano
                        </Link>
                    </div>
                )

            default:
                return (
                    <div>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            Status: {status.status}
                        </p>
                        <Link
                            to="/assinar"
                            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Ver Planos
                        </Link>
                    </div>
                )
        }
    }

    if (status.status === 'sem_assinatura') {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6 lg:mb-8">
                <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-6 h-6 text-orange-500" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Sem Assinatura Ativa
                    </h3>
                </div>
                {getStatusMessage()}
            </div>
        )
    }

    return (
        <div className={`bg-white dark:bg-gray-800 rounded-lg border p-6 mb-6 lg:mb-8 ${getStatusColor()}`}>
            <div className="flex items-center gap-3 mb-4">
                {getStatusIcon()}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Status da Assinatura
                </h3>
            </div>

            {assinatura && (
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                        {getPlanoIcon(assinatura.plano.nome)}
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                            {assinatura.plano.nome_display}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">
                            - R$ {assinatura.valor_mensal.toFixed(2)}/mês
                        </span>
                    </div>
                </div>
            )}

            {getStatusMessage()}
        </div>
    )
}
